import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { CopyableCode } from '../../components/ui/CopyableCode';
import { num } from '../../format';
import { PrintDialog } from '../labels/PrintDialog';
import { LocationPicker } from '../locations/LocationPicker';
import {
  issuePackage,
  measurePackageStocktake,
  movePackage,
  setPackageAvailability,
  splitPackage,
  stockPackages,
  stocktakePackage,
  type PackageAvailability,
} from './api';

type QuantityAction = 'issue' | 'split' | 'count' | 'measure';

/** A compact package card deliberately sits beside aggregate stock: the former
 * answers “which reel?”, while the latter retains the familiar total. */
export function PackagesPanel({ componentId, focusId }: { componentId: string; focusId?: string | null }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(Boolean(focusId));
  const [printing, setPrinting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [destinations, setDestinations] = useState<Record<string, string | null>>({});
  const [measurement, setMeasurement] = useState<string | null>(null);
  const [action, setAction] = useState<{ id: string; kind: QuantityAction } | null>(null);
  const [actionValue, setActionValue] = useState('');
  const packages = useQuery({
    queryKey: ['stock', 'packages', componentId],
    queryFn: () => stockPackages(componentId),
    // The normal component card stays an aggregate view. A scanned package is
    // the one exception: it opens this panel so its physical record is visible.
    enabled: expanded,
  });

  useEffect(() => {
    if (focusId) setExpanded(true);
  }, [focusId]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['stock', 'packages', componentId] });
    void queryClient.invalidateQueries({ queryKey: ['stock'] });
    void queryClient.invalidateQueries({ queryKey: ['components', componentId] });
  };
  const status = useMutation({ mutationFn: ({ id, value }: { id: string; value: PackageAvailability }) => setPackageAvailability(id, value), onSuccess: refresh, onError: () => setError('unknown') });
  const issue = useMutation({ mutationFn: ({ id, qty }: { id: string; qty: string }) => issuePackage(id, qty), onSuccess: refresh, onError: () => setError('unknown') });
  const move = useMutation({ mutationFn: ({ id, location }: { id: string; location: string }) => movePackage(id, location), onSuccess: refresh, onError: () => setError('unknown') });
  const split = useMutation({ mutationFn: ({ id, qty }: { id: string; qty: string }) => splitPackage(id, qty), onSuccess: refresh, onError: () => setError('unknown') });
  const count = useMutation({ mutationFn: ({ id, qty }: { id: string; qty: string }) => stocktakePackage(id, qty), onSuccess: refresh, onError: () => setError('unknown') });
  const measure = useMutation({ mutationFn: ({ id, value }: { id: string; value: string }) => measurePackageStocktake(id, value), onSuccess: (result) => { setMeasurement(`lots.tareSource.${result.tare_source}`); refresh(); }, onError: () => setError('unknown') });

  const start = (id: string, kind: QuantityAction, currentQuantity: string) => {
    setError(null);
    setAction({ id, kind });
    setActionValue(kind === 'count' ? currentQuantity : '');
  };

  const submitAction = () => {
    if (!action || !actionValue.trim()) return;
    if (action.kind === 'issue') issue.mutate({ id: action.id, qty: actionValue });
    else if (action.kind === 'split') split.mutate({ id: action.id, qty: actionValue });
    else if (action.kind === 'count') count.mutate({ id: action.id, qty: actionValue });
    else measure.mutate({ id: action.id, value: actionValue });
    setAction(null);
  };

  return <section className="stock packages-panel">
    <header className="packages-panel__head">
      <h3>{t('lots.title')}</h3>
      <button type="button" className="linklike" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
        {t(expanded ? 'lots.hide' : 'lots.show')}
      </button>
    </header>
    {expanded && <>
      {packages.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {error && <p className="error">{t(`lots.errors.${error}`)}</p>}
      {(packages.data ?? []).map((row) => {
        const focused = row.id === focusId;
        const currentAction = action?.id === row.id ? action : null;
        return <article key={row.id} className={focused ? 'packages-panel__row is-focused' : 'packages-panel__row'}>
          <div className="packages-panel__summary">
            <strong>{num(row.quantity)}</strong>
            <CopyableCode value={row.code} label={t('lots.packageCode')} />
            <CopyableCode value={row.lot_code} label={t('lots.lotCode')} />
            <span>{row.location_name ?? t('lots.outside')}</span>
            <small>{[row.vendor_name, row.external_lot_number, row.date_code].filter(Boolean).join(' · ') || t('lots.opening')}</small>
            {row.tare_weight_g !== null && <small>{t('lots.tare', { value: num(row.tare_weight_g) })}</small>}
          </div>
          <div className="packages-panel__controls">
            <label className="packages-panel__availability">
              <span>{t('lots.availabilityLabel')}</span>
              <select value={row.availability} onChange={(event) => status.mutate({ id: row.id, value: event.target.value as PackageAvailability })}>
                {(['released', 'quarantine', 'rejected'] as PackageAvailability[]).map((value) => <option key={value} value={value}>{t(`lots.availability.${value}`)}</option>)}
              </select>
            </label>
            <div className="packages-panel__actions">
              <button type="button" onClick={() => start(row.id, 'issue', row.quantity)} disabled={row.availability !== 'released'}>{t('lots.issue')}</button>
              <button type="button" onClick={() => start(row.id, 'split', row.quantity)}>{t('lots.split')}</button>
              <button type="button" onClick={() => start(row.id, 'count', row.quantity)}>{t('lots.count')}</button>
              <button type="button" onClick={() => setPrinting(row.id)}>{t('labels.print')}</button>
            </div>
          </div>
          {currentAction && <form className="packages-panel__action-form" onSubmit={(event) => { event.preventDefault(); submitAction(); }}>
            <label>
              {t(`lots.${currentAction.kind}Prompt`)}
              <input autoFocus value={actionValue} onChange={(event) => setActionValue(event.target.value)} inputMode="decimal" />
            </label>
            <button type="submit">{t(`lots.${currentAction.kind}`)}</button>
            <button type="button" onClick={() => setAction(null)}>{t('common.cancel')}</button>
          </form>}
          <details className="packages-panel__more">
            <summary>{t('lots.moreActions')}</summary>
            <div className="packages-panel__more-content">
              <button type="button" onClick={() => start(row.id, 'measure', row.quantity)}>{t('lots.measure')}</button>
              <form className="packages-panel__move" onSubmit={(event) => { event.preventDefault(); const location = destinations[row.id]; if (location) move.mutate({ id: row.id, location }); }}>
                <label>
                  {t('lots.moveTo')}
                  <LocationPicker label={t('lots.moveTo')} value={destinations[row.id] ?? null} onChange={(location) => setDestinations((current) => ({ ...current, [row.id]: location }))} allowOutside={false} componentId={componentId} />
                </label>
                <button type="submit" disabled={!destinations[row.id] || move.isPending}>{t('lots.move')}</button>
              </form>
            </div>
          </details>
        </article>;
      })}
      {!packages.isLoading && (packages.data ?? []).length === 0 && <p className="panel__hint">{t('lots.empty')}</p>}
      {measurement && <p className="panel__hint">{t(measurement)}</p>}
    </>}
    {printing && <PrintDialog entityKind="package" entityId={printing} onClose={() => setPrinting(null)} />}
  </section>;
}
